import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Grid, Paper, Button } from '@material-ui/core';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import GitHubIcon from '@material-ui/icons/GitHub';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  section: {
    padding: theme.spacing(12, 0),
    background: 'linear-gradient(135deg, rgba(15, 32, 39, 0.95) 0%, rgba(32, 58, 67, 0.95) 25%, rgba(44, 83, 100, 0.95) 50%, rgba(40, 116, 166, 0.95) 75%, rgba(0, 245, 255, 0.1) 100%)',
    position: 'relative',
    color: '#fff',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 80%, rgba(0, 245, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(0, 245, 255, 0.03) 60deg, transparent 120deg, rgba(138, 43, 226, 0.03) 180deg, transparent 240deg, rgba(0, 245, 255, 0.03) 300deg, transparent 360deg)',
      animation: 'rotate 20s linear infinite',
      pointerEvents: 'none',
    },
  },
  '@keyframes rotate': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  topShadow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '30px',
    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.7), transparent)',
    zIndex: 1,
  },
  bottomShadow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '30px',
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
    zIndex: 1,
  },
  container: {
    position: 'relative',
    zIndex: 2,
  },
  paper: {
    padding: theme.spacing(6),
    textAlign: 'center',
    background: 'rgba(15, 25, 35, 0.15)',
    borderRadius: '24px',
    border: '1px solid rgba(0, 245, 255, 0.2)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    color: '#fff',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 32px 64px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 245, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
      border: '1px solid rgba(0, 245, 255, 0.4)',
    },
  },
  title: {
    textAlign: 'center',
    fontFamily: '"Roboto", sans-serif',
    fontWeight: 700,
    fontSize: '3rem',
    marginBottom: theme.spacing(4),
    color: '#00f5ff',
    textShadow: '0 0 20px rgba(0, 245, 255, 0.5)',
    position: 'relative',
    display: 'inline-block',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '4px',
      background: 'linear-gradient(90deg, transparent, #00f5ff, transparent)',
      borderRadius: '2px',
      animation: 'glow 2s ease-in-out infinite alternate',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  '@keyframes glow': {
    '0%': { boxShadow: '0 0 10px rgba(0, 245, 255, 0.5)' },
    '100%': { boxShadow: '0 0 20px rgba(0, 245, 255, 0.8)' },
  },
  subtitle: {
    fontSize: '1.2rem',
    marginBottom: theme.spacing(4),
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: 300,
    letterSpacing: '1px',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(4),
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(3),
    },
  },
  button: {
    width: '180px',
    height: '180px',
    borderRadius: '24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(15, 25, 35, 0.2)',
    border: '1px solid rgba(0, 245, 255, 0.3)',
    backdropFilter: 'blur(15px)',
    color: '#fff',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(0, 245, 255, 0.1), transparent)',
      transition: 'left 0.6s',
    },
    '&:hover': {
      transform: 'translateY(-8px) scale(1.05)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 245, 255, 0.2)',
      border: '1px solid rgba(0, 245, 255, 0.6)',
      '&::before': {
        left: '100%',
      },
    },
    '&:nth-child(1)': {
      '&:hover': {
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(0, 119, 181, 0.4)',
        border: '1px solid rgba(0, 119, 181, 0.6)',
      },
    },
    '&:nth-child(2)': {
      '&:hover': {
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 30px rgba(255, 255, 255, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
      },
    },
    [theme.breakpoints.down('sm')]: {
      width: '160px',
      height: '160px',
    },
  },
  icon: {
    fontSize: '3.5rem',
    marginBottom: theme.spacing(1),
    transition: 'all 0.3s ease',
    filter: 'drop-shadow(0 0 10px rgba(0, 245, 255, 0.3))',
  },
  linkedinIcon: {
    color: '#0077b5',
  },
  githubIcon: {
    color: '#fff',
  },
}));

const StayConnected = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <section className={classes.section}>
      <div className={classes.topShadow}></div>
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h2" className={classes.title}>
          {t('stayConnected.title')}
        </Typography>
        <Grid container spacing={3} justify="center">
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="h5" className={classes.subtitle}>
                {t('stayConnected.subtitle')}
              </Typography>
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  className={classes.button}
                  href="https://www.linkedin.com/in/carlos-ignacio-aguilera-patalano-9a967733/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon className={`${classes.icon} ${classes.linkedinIcon}`} />
                  <span>{t('stayConnected.buttons.linkedin')}</span>
                </Button>
                <Button
                  variant="contained"
                  className={classes.button}
                  href="https://github.com/ciap35/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GitHubIcon className={`${classes.icon} ${classes.githubIcon}`} />
                  <span>{t('stayConnected.buttons.github')}</span>
                </Button>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      <div className={classes.bottomShadow}></div>
    </section>
  );
};

export default StayConnected;
